import React from "react";
import { useForm } from "react-hook-form";
import { X, ChevronDown, MapPin } from "lucide-react";
import {
  Agent,
  CreateAgentRequest,
  UpdateAgentRequest,
} from "../../api/services/agents/type";
import CustomInput from "../ui/CustomInput";
import PrimaryButton from "../ui/PrimaryButton";
import { ReactComponent as UserIcon } from "../../assets/ic_user.svg";
import { ReactComponent as MailIcon } from "../../assets/ic_mail.svg";
import { ReactComponent as PhoneIcon } from "../../assets/ic_phone.svg";

interface AgentFormProps {
  agent?: Agent;
  isLoading?: boolean;
  onSubmit: (data: CreateAgentRequest | UpdateAgentRequest) => void;
  onCancel: () => void;
}

export const AgentForm: React.FC<AgentFormProps> = ({
  agent,
  isLoading = false,
  onSubmit,
  onCancel,
}) => {
  const isEditMode = !!agent;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm<CreateAgentRequest>({
    defaultValues: {
      name: agent?.name || "",
      email: agent?.email || "",
      phone: agent?.phone || "",
      address: agent?.address || "",
      agentType: agent?.agentType || "NORMAL",
      commissionPercent: agent?.commissionPercent || 0,
      sharePercentage: agent?.sharePercentage || 0,
    },
  });

  const watchedAgentType = watch("agentType");

  React.useEffect(() => {
    if (agent) {
      setValue("name", agent.name);
      setValue("email", agent.email);
      setValue("phone", agent.phone);
      setValue("agentType", agent.agentType);
      setValue("address", agent.address || "");
      setValue("commissionPercent", agent.commissionPercent);
      setValue("sharePercentage", agent.sharePercentage || 0);
    } else {
      reset();
    }
  }, [agent, setValue, reset]);

  const onFormSubmit = (data: CreateAgentRequest) => {
    const formattedData = {
      ...data,
      commissionPercent: Number(data.commissionPercent),
      sharePercentage:
        data.agentType === "JUNKET" ? Number(data.sharePercentage) : undefined,
    };
    onSubmit(formattedData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-lg w-full max-w-[700px] max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-800 p-6 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">
            {isEditMode ? "Edit Agent" : "Add New Agent"}
          </h2>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-white transition-colors"
            disabled={isLoading}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-6">
          <div className="space-y-4">
            <CustomInput
              label="Full Name"
              type="text"
              placeholder="Enter agent's full name"
              register={register}
              name="name"
              validation={{
                required: "Name is required",
                validate: (value: string | number | undefined) => {
                  if (typeof value !== "string") return "Name is required";
                  const trimmed = value.trim();
                  if (trimmed.length === 0) return "Name is required";
                  if (/\d/.test(trimmed)) return "Name cannot contain numbers";
                  if (trimmed.length < 2)
                    return "Name must be at least 2 characters";
                  return true;
                },
              }}
              error={errors.name}
              disabled={isLoading}
              icon={<UserIcon className="w-5 h-5 text-slate-400" />}
            />

            <div className="flex justify-between gap-4">
              <CustomInput
                label="Email Address (Optional)"
                type="text"
                placeholder="Enter email address (Optional)"
                register={register}
                name="email"
                validation={{
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Please enter a valid email address",
                  },
                }}
                error={errors.email}
                disabled={isLoading}
                icon={<MailIcon className="w-5 h-5 text-slate-400" />}
              />
              <CustomInput
                label="Address (Optional)"
                type="text"
                placeholder="Enter address (Optional)"
                register={register}
                name="address"
                error={errors.address}
                disabled={isLoading}
                icon={<MapPin className="w-5 h-5 text-slate-400" />}
              />
            </div>

            <div className="flex justify-between gap-4">
              <div className="basis-1/2">
                <CustomInput
                  label="Phone Number"
                  type="tel"
                  placeholder="Enter phone number"
                  register={register}
                  name="phone"
                  validation={{
                    required: "Phone number is required",
                    validate: (value: string | number | undefined) => {
                      if (typeof value !== "string")
                        return "Phone number is required";
                      const trimmed = value.trim();
                      if (trimmed.length === 0)
                        return "Phone number is required";
                      if (!/^\d+$/.test(trimmed))
                        return "Phone must contain numbers only";
                      if (trimmed.length < 7)
                        return "Phone number must be at least 7 digits";
                      return true;
                    },
                  }}
                  error={errors.phone}
                  disabled={isLoading}
                  icon={<PhoneIcon className="w-5 h-5 text-slate-400" />}
                />
              </div>

              <div className="basis-1/2">
                {/* Agent Type Dropdown */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-300">
                    Agent Type
                  </label>
                  <div className="relative">
                    <select
                      {...register("agentType", {
                        required: "Agent type is required",
                      })}
                      disabled={isLoading}
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-10"
                    >
                      <option value="NORMAL">Normal Agent</option>
                      <option value="JUNKET">Junket Agent</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5 pointer-events-none" />
                  </div>
                  {errors.agentType && (
                    <p className="text-red-400 text-sm">
                      {errors.agentType.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Commission and Share Section */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-300">
                  Commission
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="Commission Percentage"
                    {...register("commissionPercent", {
                      required: "Commission percentage is required",
                      min: {
                        value: 0,
                        message: "Commission must be at least 0%",
                      },
                      max: {
                        value: 100,
                        message: "Commission cannot exceed 100%",
                      },
                    })}
                    disabled={isLoading}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 text-sm">
                    %
                  </span>
                </div>
                {errors.commissionPercent && (
                  <p className="text-red-400 text-sm">
                    {errors.commissionPercent.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-300">
                  Share
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="Share Percentage"
                    {...register("sharePercentage", {
                      required:
                        watchedAgentType === "JUNKET"
                          ? "Share percentage is required for junket agents"
                          : false,
                      min: { value: 0, message: "Share must be at least 0%" },
                      max: { value: 100, message: "Share cannot exceed 100%" },
                    })}
                    disabled={isLoading || watchedAgentType === "NORMAL"}
                    className={`w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8 ${
                      watchedAgentType === "NORMAL"
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 text-sm">
                    %
                  </span>
                </div>
                {errors.sharePercentage && (
                  <p className="text-red-400 text-sm">
                    {errors.sharePercentage.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <PrimaryButton
              type="submit"
              isLoading={isLoading}
              loadingText={isEditMode ? "Updating..." : "Creating..."}
              className="flex-1"
            >
              {isEditMode ? "Update Agent" : "Create Agent"}
            </PrimaryButton>
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 hover:border-slate-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
